/**
 * The Earthworks tab of QuickBooks mapping (S10, fieldiq-api GET
 * /earthworks/qbo/mapping-needs). Earthworks has its OWN item keys — `ew:labour`,
 * `ew:equipment`, `ew:holdback`, `ew:deposit` … — so a casing binding for the
 * same word never prices an earthworks line, and changing one never moves the
 * other. Bindings save through the same PUT /quickbooks/mappings as every tab.
 *
 * Three lists, each something of ours on the left and the realm's object on
 * the right, saved on change:
 *   Line items   ew: item keys on unpushed sent / paid invoices
 *   Tax codes    `<JUR>:<label>`, plus `<JUR>:EXEMPT` — the non-taxable code a
 *                DEPOSIT line posts on in a Canadian company (a deposit carries
 *                no tax; the US realm needs none)
 *   Customers    the earthworks customers, bound to an existing QuickBooks
 *                customer — never created, never matched by name
 *
 * A binding to an id this realm does not list is shown as such, never as the
 * first option (the same three-state rule as the other tabs).
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { useEwQboMappingNeeds } from '../../hooks/useEwInvoices';
import type { EwQboBinding } from '../../types/ew-invoices';
import type { QboCustomerSummary, QboItemSummary, QboTaxCodeSummary } from '../../types/qbo-mapping';

type MapKind = 'item' | 'tax_code' | 'customer';
interface Candidate { id: string; label: string; active: boolean }
interface Row { key: string; label: string; sub: string | null; binding: EwQboBinding | null }

function unwrap<T>(body: { data?: T } | T | undefined): T | undefined {
  if (body && typeof body === 'object' && 'data' in body) return (body as { data?: T }).data;
  return body as T | undefined;
}

const ITEM_LABEL: Record<string, string> = {
  'ew:holdback': 'Holdback / retainage withheld (a negative line)',
  'ew:deposit': 'Deposit credited (a negative line)',
};
const itemLabel = (key: string) =>
  ITEM_LABEL[key] ?? `Earthworks — ${key.replace(/^ew:/, '').replace(/_/g, ' ')}`;

function MapTable({ title, blurb, kind, rows, candidates, onSave, onRemove, saving, testId }: {
  title: string; blurb: string; kind: MapKind; rows: Row[]; candidates: Candidate[] | undefined;
  onSave: (kind: MapKind, key: string, qboId: string) => void; onRemove: (kind: MapKind, key: string) => void;
  saving: boolean; testId: string;
}) {
  const ids = useMemo(() => new Set((candidates ?? []).map((c) => c.id)), [candidates]);
  const stale = (r: Row) => candidates !== undefined && !!r.binding && !ids.has(r.binding.qboId);
  return (
    <section className="space-y-2" data-testid={testId}>
      <h3 className="text-base font-bold text-white">{title}</h3>
      <p className="text-sm text-fieldiq-muted max-w-3xl">{blurb}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-fieldiq-muted border-b border-fieldiq-border">
              <th className="py-2 pr-4 font-medium">In FieldIQ</th>
              <th className="py-2 pr-4 font-medium">QuickBooks object</th>
              <th className="py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={3} className="py-4 text-fieldiq-muted">Nothing here needs mapping yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-fieldiq-border/50" data-testid={`ew-qbo-row-${r.key}`}>
                <td className="py-2 pr-4 text-white">
                  {r.label}
                  {r.sub && <span className="ml-2 text-xs text-fieldiq-muted">{r.sub}</span>}
                </td>
                <td className="py-2 pr-4">
                  <select
                    aria-label={`QuickBooks object for ${r.label}`}
                    value={r.binding?.qboId ?? ''}
                    disabled={saving || candidates === undefined}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) { onRemove(kind, r.key); return; }
                      if (r.binding && r.binding.qboId !== v && !window.confirm(
                        `Replace the QuickBooks mapping for "${r.label}"?\n\nFrom: ${r.binding.qboName ?? `#${r.binding.qboId}`}\n` +
                        `To:   ${(candidates ?? []).find((c) => c.id === v)?.label ?? `#${v}`}`)) {
                        e.target.value = r.binding.qboId;
                        return;
                      }
                      onSave(kind, r.key, v);
                    }}
                    className={`bg-fieldiq-dark border rounded-lg px-2 py-1.5 text-white focus:border-fieldiq-accent focus:outline-none min-w-[16rem] ${stale(r) ? 'border-red-500' : 'border-fieldiq-border'}`}
                  >
                    <option value="">— not mapped —</option>
                    {stale(r) && r.binding && (
                      <option value={r.binding.qboId} disabled>
                        {`⚠ #${r.binding.qboId}${r.binding.qboName ? ` (${r.binding.qboName})` : ''} — not found in this QuickBooks company`}
                      </option>
                    )}
                    {(candidates ?? []).map((c) => (
                      <option key={c.id} value={c.id}>{c.label}{c.active ? '' : '  (archived in QuickBooks)'}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2">
                  {stale(r)
                    ? <span className="flex items-center gap-1 text-red-400 text-xs"><AlertCircle size={13} /> not in this company</span>
                    : r.binding
                      ? <span className="flex items-center gap-1 text-green-400 text-xs"><Check size={13} /> mapped</span>
                      : <span className="flex items-center gap-1 text-amber-400 text-xs"><AlertCircle size={13} /> blocks a push</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function EwQboMapping({ notify }: { notify: (text: string, tone?: 'success' | 'error') => void }) {
  const qc = useQueryClient();
  const needs = useEwQboMappingNeeds();
  const connected = needs.data?.connected === true;
  const items = useQuery({
    queryKey: ['qbo', 'ew-candidates', 'item'], enabled: connected,
    queryFn: async (): Promise<Candidate[]> => (unwrap((await api.quickbooks.mappings.realmItems()).data) ?? [])
      .map((r: QboItemSummary) => ({ id: r.id, label: r.fullyQualifiedName || r.name, active: r.active })),
  });
  const taxCodes = useQuery({
    queryKey: ['qbo', 'ew-candidates', 'tax_code'], enabled: connected,
    queryFn: async (): Promise<Candidate[]> => (unwrap((await api.quickbooks.mappings.realmTaxCodes()).data) ?? [])
      .map((r: QboTaxCodeSummary) => ({ id: r.id, label: r.name, active: r.active !== false })),
  });
  const customers = useQuery({
    queryKey: ['qbo', 'ew-candidates', 'customer'], enabled: connected,
    queryFn: async (): Promise<Candidate[]> => (unwrap((await api.quickbooks.mappings.realmCustomers()).data) ?? [])
      .map((r: QboCustomerSummary) => ({ id: r.id, label: r.displayName, active: r.active })),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['qbo'] });
    qc.invalidateQueries({ queryKey: ['earthworks', 'qbo-mapping-needs'] });
  };
  const save = useMutation({
    mutationFn: (v: { kind: MapKind; key: string; qboId: string }) =>
      api.quickbooks.mappings.save({ kind: v.kind, local_key: v.key, qbo_id: v.qboId }),
    onSuccess: () => { refresh(); notify('Mapping saved'); },
    onError: (err: any) => notify(err?.response?.data?.error || 'Failed to save mapping', 'error'),
  });
  const remove = useMutation({
    mutationFn: (v: { kind: MapKind; key: string }) => api.quickbooks.mappings.remove(v.kind, v.key),
    onSuccess: () => { refresh(); notify('Mapping removed'); },
    onError: (err: any) => notify(err?.response?.data?.error || 'Failed to remove mapping', 'error'),
  });

  if (needs.isLoading) {
    return <div className="flex items-center gap-2 text-fieldiq-muted text-sm"><Loader2 size={16} className="animate-spin" /> Loading…</div>;
  }
  if (needs.isError || !needs.data) {
    return <p role="alert" className="text-sm text-red-400">The earthworks mapping list could not be loaded.</p>;
  }
  if (!connected) {
    return <p className="text-sm text-fieldiq-muted" data-testid="ew-qbo-not-connected">QuickBooks is not connected.</p>;
  }

  const d = needs.data;
  const busy = save.isPending || remove.isPending;
  const onSave = (kind: MapKind, key: string, qboId: string) => save.mutate({ kind, key, qboId });
  const onRemove = (kind: MapKind, key: string) => remove.mutate({ kind, key });
  const unmapped = d.items.filter((r) => !r.binding).length + d.taxCodes.filter((r) => !r.binding).length
    + d.customers.filter((r) => !r.binding).length;
  const total = d.items.length + d.taxCodes.length + d.customers.length;

  return (
    <div className="space-y-6" data-testid="ew-qbo-mapping">
      <p className="text-xs text-fieldiq-muted" data-testid="ew-qbo-unmapped-count">
        {total === 0 ? 'No sent earthworks invoice is waiting to be pushed.'
          : unmapped === 0 ? 'Everything here is mapped'
          : `${unmapped} of ${total} still ${unmapped === 1 ? 'needs' : 'need'} a mapping`}
      </p>
      <MapTable
        title="Line items" testId="ew-qbo-items" kind="item" candidates={items.data} saving={busy} onSave={onSave} onRemove={onRemove}
        blurb="Earthworks' own item keys. Holdback and deposit are negative lines, so QuickBooks' total equals the invoice's — bind each to the item your bookkeeper uses for it (often a liability or 'other current' account)."
        rows={d.items.map((r) => ({ key: r.key, label: itemLabel(r.key), sub: `${r.invoices} invoice${r.invoices === 1 ? '' : 's'}`, binding: r.binding }))}
      />
      <MapTable
        title="Tax codes" testId="ew-qbo-tax" kind="tax_code" candidates={taxCodes.data} saving={busy} onSave={onSave} onRemove={onRemove}
        blurb="One code per province and label. The EXEMPT row is the non-taxable code a deposit line posts on in a Canadian company — usually 'Exempt' or 'Out of scope'."
        rows={d.taxCodes.map((r) => ({ key: r.key, label: r.key, sub: r.depositOnly ? 'deposit line' : null, binding: r.binding }))}
      />
      <MapTable
        title="Customers" testId="ew-qbo-customers" kind="customer" candidates={customers.data} saving={busy} onSave={onSave} onRemove={onRemove}
        blurb="Which QuickBooks customer each earthworks customer is. FieldIQ never creates one or matches by name."
        rows={d.customers.map((r) => ({ key: r.customerId, label: r.name, sub: null, binding: r.binding }))}
      />
    </div>
  );
}
